import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { MotifSelect, MotifOption } from "@ey-xd/motif-react";
import "./CheckpointConfirmCard.scss";
import ActiveDirectoryUserSearch from "../../../../../OrdersDetails/ActiveDirectoryUserSearch";

/**
 * CheckpointConfirmCard - Renders a form for confirming checkpoint fields
 * Used when AI response has checkpoint_pending: true and ui_fields array
 */
export const CheckpointConfirmCard = ({
  uiFields = [],
  onConfirm,
  isSubmitting = false,
  submitError = null,
  submitSuccess = false,
}) => {
  // Get user from Redux
  const user = useSelector((state) => state.user?.user);
  const userEmail = user?.email || user?.mail || "";

  // Initialize form values from ui_fields current_value
  const [formValues, setFormValues] = useState(() => {
    const initial = {};
    uiFields.forEach((field) => {
      initial[field.field_name] = field.current_value || "";
    });
    return initial;
  });

  // Track validation errors
  const [errors, setErrors] = useState({});

  // Update form values when uiFields change
  useEffect(() => {
    const initial = {};
    uiFields.forEach((field) => {
      initial[field.field_name] = field.current_value || "";
    });
    setFormValues(initial);
  }, [uiFields]);

  // Handle input change
  const handleInputChange = useCallback((fieldName, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    // Clear error when user types
    setErrors((prev) => {
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  // Handle AD user selection - receives email string or user object
  const handleUserSelect = useCallback((fieldName, valueOrUser) => {
    // ActiveDirectoryUserSearch passes email string directly from onChange
    const email =
      typeof valueOrUser === "string"
        ? valueOrUser
        : valueOrUser?.email ||
          valueOrUser?.mail ||
          valueOrUser?.userPrincipalName ||
          "";

    setFormValues((prev) => ({
      ...prev,
      [fieldName]: email,
    }));

    if (email) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
  }, []);

  // Validate all required fields
  const validateFields = useCallback(() => {
    const newErrors = {};
    uiFields.forEach((field) => {
      if (field.required && !formValues[field.field_name]?.trim()) {
        newErrors[field.field_name] =
          `${field.label || field.field_name} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [uiFields, formValues]);

  // Handle confirm button click
  const handleConfirm = useCallback(() => {
    if (validateFields() && onConfirm) {
      // Build resume_input object
      const resumeInput = {};
      uiFields.forEach((field) => {
        resumeInput[field.field_name] = formValues[field.field_name] || "";
      });
      onConfirm(resumeInput);
    }
  }, [validateFields, onConfirm, uiFields, formValues]);

  // Every field must have a value before confirming, not just the required ones
  const isFormValid = useMemo(() => {
    return uiFields.every(
      (field) => !!String(formValues[field.field_name] ?? "").trim(),
    );
  }, [uiFields, formValues]);

  // Render field based on type
  const renderField = (field) => {
    const { field_name, type, display_label, options_source, required } = field;
    const value = formValues[field_name] || "";
    const error = errors[field_name];

    // Active Directory field
    if (
      field?.meta?.input_type === "active_directory" ||
      field?.meta?.options_source?.type === "active_directory" ||
      field?.input_type === "active_directory" ||
      field?.type === "active_directory"
    ) {
      return (
        <div key={field_name} className="checkpoint-field">
          <label className="checkpoint-field-label">
            {field?.meta?.display_label || field?.display_label || field_name}
            {required && <span className="required-indicator">*</span>}
          </label>
          <div className="checkpoint-field-input-wrapper">
            <ActiveDirectoryUserSearch
              value={value}
              onChange={(selectedUser) =>
                handleUserSelect(field_name, selectedUser)
              }
              placeholder={`Search for ${field?.meta?.display_label || field?.display_label || field_name}...`}
              className="checkpoint-ad-search"
              disabled={isSubmitting || submitSuccess}
            />
          </div>
          {error && <span className="checkpoint-field-error">{error}</span>}
        </div>
      );
    }

    // Dropdown field with options
    if (
      field?.meta?.input_type === "dropdown" ||
      field?.meta?.input_type === "select" ||
      field?.input_type === "dropdown"|| field?.type === "dropdown" || field?.type === "disambiguation"
    ) {
      const options = field?.options || field?.fieldValueList || [];
      return (
        <div key={field_name} className="checkpoint-field">
          <label className="checkpoint-field-label">
            {field?.meta?.display_label || field?.display_label || field_name}
            {required && <span className="required-indicator">*</span>}
          </label>
          <div className="checkpoint-field-select-wrapper">
            <MotifSelect
              className="checkpoint-field-select"
              value={value}
              onChange={(selectedValue) => handleInputChange(field_name, selectedValue)}
              disabled={isSubmitting || submitSuccess}
              placeholder={`Select ${field?.meta?.display_label || field?.display_label || field_name}`}
            >
              {options.map((opt, idx) => {
                const optValue =
                  typeof opt === "object" ? opt.value || opt.id || opt : opt;
                const optLabel =
                  typeof opt === "object"
                    ? opt.label || opt.name || opt.value || opt
                    : opt;
                return (
                  <MotifOption key={idx} value={optValue}>
                    {optLabel}
                  </MotifOption>
                );
              })}
            </MotifSelect>
          </div>
          {error && <span className="checkpoint-field-error">{error}</span>}
        </div>
      );
    }

    // Date field
    if (field?.meta?.input_type === "date" || field?.input_type === "date") {
      return (
        <div key={field_name} className="checkpoint-field">
          <label className="checkpoint-field-label">
            {field?.meta?.display_label || field?.display_label || field_name}
            {required && <span className="required-indicator">*</span>}
          </label>

          <input
            type="date"
            className="checkpoint-field-input"
            value={value}
            onClick={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onFocus={(event) => {
              try {
                event.currentTarget.showPicker?.();
              } catch (error) {}
            }}
            onKeyDown={(event) => {
              if (!["Tab", "Shift", "Escape"].includes(event.key)) {
                event.preventDefault();
              }
            }}
            onChange={(e) => handleInputChange(field_name, e.target.value)}
            inputMode="none"
            disabled={isSubmitting || submitSuccess}
          />
          {error && <span className="checkpoint-field-error">{error}</span>}
        </div>
      );
    }

    // Default text input
    return (
      <div key={field_name} className="checkpoint-field">
        <label className="checkpoint-field-label">
          {field?.meta?.display_label || field?.display_label || field_name}
          {required && <span className="required-indicator">*</span>}
        </label>
        <input
          type="text"
          className="checkpoint-field-input"
          value={value}
          onChange={(e) => handleInputChange(field_name, e.target.value)}
          disabled={isSubmitting || submitSuccess}
        />
        {error && <span className="checkpoint-field-error">{error}</span>}
      </div>
    );
  };

  if (!uiFields || uiFields.length === 0) {
    return null;
  }

  return (
    <div className="checkpoint-confirm-card">
      <div className="checkpoint-confirm-header">
        <span className="checkpoint-confirm-title">Confirm Details</span>
      </div>

      <div className="checkpoint-fields-container">
        {uiFields.map((field) => renderField(field))}
      </div>

      {submitError && (
        <div className="checkpoint-error-message">{submitError}</div>
      )}

      {submitSuccess && (
        <div className="checkpoint-success-message">
          Details confirmed successfully!
        </div>
      )}

      <div className="checkpoint-actions">
        <button
          className="checkpoint-confirm-btn"
          onClick={handleConfirm}
          disabled={isSubmitting || !isFormValid || submitSuccess}
        >
          {isSubmitting ? "Confirming..." : "Confirm"}
        </button>
      </div>
    </div>
  );
};

export default CheckpointConfirmCard;
